import { useRef, useState, type ChangeEvent } from 'react';
import { Alert, Button, FileThumb, FormField, FormPage, Select, StickyActionBar, Textarea } from '@nexvia-logistics/ui';
import { Trash } from '@nexvia-logistics/ui/icons';
import type { Department, LocalitiesByDepartment } from '@/shared/locations-catalog';
import type { AddressSearch } from '@/shared/google-places';
import {
  describeApiError,
  knownApiErrorMessage,
  type StorageLocation,
  type StorageLocationCoordinates,
  type StorageLocationInput
} from '@/shared/storage-locations-api';
import { AddressAutocompleteField } from '@/pages/AddressAutocompleteField';

/**
 * Mirrors the API's location-photo list (nexvia-api-web `storage.constants.ts`
 * `LOCATION_PHOTO_ALLOWED_MIME_TYPES`: shipment's `ALLOWED_IMAGE_MIME_TYPES` minus HEIC/HEIF) and
 * the same 10 MB ceiling. NEX-1165 (W2-hard-a): no HEIC — the API answers 422
 * `STORAGE_PHOTO_HEIC_NOT_SUPPORTED` because browsers other than Safari cannot show it to the client.
 */
export const ALLOWED_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const HEIC_PHOTO_MIME_TYPES = ['image/heic', 'image/heif'];
const HEIC_FILE_NAME = /\.hei[cf]$/i;
export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;
/**
 * NEX-1165 — 10 photos per location, the same cap the API enforces (nexvia-api-web
 * `domain/storage-location.validators.ts`, 400 `STORAGE_LOCATION_PHOTOS_TOO_MANY` above 10; plan
 * 2026-10-15 Fase 2.1). These are the photos the client sees next to each quote (business doc §5).
 * Change both sides together. Not tied to a request-body size: photos upload through
 * `onUploadPhoto`/`uploadStorageLocationPhoto`, one request per photo to the API, which stores it.
 */
export const MAX_PHOTOS = 10;

/** NEX-1168 — the coordinates a location was saved with, when it has both. */
function coordinatesOf(location: StorageLocation | undefined): StorageLocationCoordinates | null {
  if (typeof location?.latitude !== 'number' || typeof location.longitude !== 'number') return null;
  return { latitude: location.latitude, longitude: location.longitude, placeId: location.placeId ?? null };
}

export interface LocationFormProps {
  initialValue?: StorageLocation;
  departments: Department[];
  localitiesByDepartment: LocalitiesByDepartment;
  /**
   * Uploads one photo file and resolves to the durable URL to store in `photos[]`. Real backend
   * call (`uploadStorageLocationPhoto` in storage-locations-api.ts) injected as a prop, same as
   * `onSubmit`/`onCancel` below: this component stays free of any `fetch(`/API import of its own,
   * per this repo's coupling contract (AGENTS.md — only the designated transport modules may call
   * `fetch` or read `env.VITE_API_URL`).
   */
  onUploadPhoto: (file: File) => Promise<string>;
  onSubmit: (input: StorageLocationInput) => Promise<void>;
  onCancel: () => void;
  /**
   * NEX-1164 design pass (spec 2026-10-18 §3.4, D-4.1): "Eliminar depósito", moved here from the
   * list card. Shown only when editing (`initialValue` given). The page keeps the confirmation and
   * the `deleteStorageLocation` call. Review fix: the form ignores clicks while it is pending (one
   * DELETE per click, both actions locked) and shows a rejection in its own error Alert, so a failed
   * delete never discards the edits not saved yet. A dismissed confirmation just resolves.
   */
  onRemove?: () => Promise<void>;
  /**
   * NEX-1168 (plan 2026-10-20 Tarea 2.7, Johan's answer 6) — Google's address search
   * (`getAddressSearch()`, injected by the page like `onUploadPhoto`). Given, the address is an
   * autocomplete: picking a suggestion stores its formatted address with its coordinates, which the
   * carrier's app navigates to. `null`/absent (no `VITE_APP_GOOGLE_MAPS_API_KEY`), the address is the
   * free-text field it always was, saved without coordinates.
   */
  addressSearch?: AddressSearch | null;
}

/**
 * **NEX-1164 (2026-10-15)**: the form is the real `@nexvia-logistics/ui` `FormPage` (its own
 * `<form>`, one `<fieldset>` per section, `submitting` disabling the whole set at once), with
 * `FormField` wiring each label to its control, `Select` for department and locality (a combobox
 * button over a portalled listbox, not a native `<select>`), `Textarea` for the address, `FileThumb`
 * for every uploaded photo (it brings its own "Quitar …" button) and `StickyActionBar` holding
 * Cancelar/Guardar inside `FormPage`'s `<form>`, so the submit button submits it. The one hand-made
 * piece left is the "Agregar fotos" trigger: no file-picker button exists in the design system
 * (spec D-4.3) — `Button` renders only a `<button>` or an `<a>` — so a `<label>` around a
 * visually-hidden (`sr-only`, still focusable) `<input type="file">` is what opens the native
 * picker, styled as the outline pill. The two actions are the real `Button` at `size="xs"` with
 * `showArrow={false}`; "Cancelar" is `variant="text"` (primary-coloured — the same cancel the
 * package's own `FormDialog` renders).
 * `formError`/`photoError` stay the real `Alert` (`tone="critical"` is what gives it `role="alert"`,
 * which this form's existing tests query).
 *
 * NEX-1168 (plan 2026-10-20 Tarea 2.7) — with `addressSearch` the address is the design system's
 * `Autocomplete` over Google's suggestions (`AddressAutocompleteField`) and a picked one travels with
 * its `latitude`/`longitude`/`placeId`; the hint under the field says whether the address is on the
 * map. Without it, the `Textarea` and the body are what they always were.
 */
export function LocationForm({
  initialValue,
  departments,
  localitiesByDepartment,
  onUploadPhoto,
  onSubmit,
  onCancel,
  onRemove,
  addressSearch = null
}: LocationFormProps) {
  const [department, setDepartment] = useState(initialValue?.department ?? '');
  const [locality, setLocality] = useState(initialValue?.locality ?? '');
  const [address, setAddress] = useState(initialValue?.address ?? '');
  // NEX-1168 — the coordinates belong to the address they were found for: any edit by hand drops
  // them, and only a picked suggestion sets them. Sent only with the autocomplete; left out of the
  // body otherwise (see `StorageLocationInput`: the API keeps the stored ones for an unchanged address
  // and clears them for a new one), so without a key the body is exactly what it always was.
  const [coordinates, setCoordinates] = useState<StorageLocationCoordinates | null>(() => coordinatesOf(initialValue));
  const located = addressSearch ? coordinates : null;
  const [photos, setPhotos] = useState<string[]>(initialValue?.photos ?? []);
  const [formError, setFormError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  // The state lags a render behind a fast double click; the ref closes the gap synchronously.
  const removingRef = useRef(false);
  const busy = submitting || removing;

  const localityOptions = department ? (localitiesByDepartment[department] ?? []) : [];
  const isEditing = initialValue !== undefined;

  const handleDepartmentChange = (nextDepartment: string) => {
    setDepartment(nextDepartment);
    // A locality belongs to exactly one department (spec §2) — carrying the old selection across
    // a department change would silently pair it with the wrong zone.
    const stillValid = (localitiesByDepartment[nextDepartment] ?? []).some((option) => option.name === locality);
    if (!stillValid) setLocality('');
  };

  const handlePhotoFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // same file can be picked again after a removal
    if (files.length === 0) return;

    setPhotoError(null);
    const accepted: string[] = [];

    for (const file of files) {
      if (photos.length + accepted.length >= MAX_PHOTOS) {
        setPhotoError(`Como máximo podés agregar ${MAX_PHOTOS} fotos.`);
        break;
      }
      if (HEIC_PHOTO_MIME_TYPES.includes(file.type.toLowerCase()) || HEIC_FILE_NAME.test(file.name)) {
        setPhotoError(`${file.name} es una foto HEIC, que no se ve en el navegador. Exportala como JPG, PNG o WEBP y subila de nuevo.`);
        continue;
      }
      if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type)) {
        setPhotoError(`${file.name} no es una imagen válida (usá JPG, PNG o WEBP).`);
        continue;
      }
      if (file.size > MAX_PHOTO_SIZE_BYTES) {
        setPhotoError(`${file.name} pesa más de ${MAX_PHOTO_SIZE_BYTES / (1024 * 1024)} MB.`);
        continue;
      }
      try {
        accepted.push(await onUploadPhoto(file));
      } catch (error) {
        // NEX-1165 — a coded answer (no connection, HEIC, too large) says what happened; never the
        // raw "Failed to fetch" of a PUT that got no answer.
        setPhotoError(knownApiErrorMessage(error) ?? `No pudimos subir ${file.name}. Probá de nuevo.`);
      }
    }

    if (accepted.length > 0) setPhotos((current) => [...current, ...accepted]);
  };

  const removePhoto = (index: number) => {
    setPhotos((current) => current.filter((_, i) => i !== index));
  };

  const typeAddress = (next: string) => {
    setAddress(next);
    setCoordinates(null);
  };

  const handleSubmit = async () => {
    const trimmedAddress = address.trim();
    if (!department || !locality || !trimmedAddress) {
      setFormError('Completá departamento, localidad y dirección antes de guardar.');
      return;
    }

    setFormError(null);
    setSubmitting(true);
    try {
      await onSubmit({ department, locality, address: trimmedAddress, photos, ...located });
    } catch (error) {
      // NEX-1165 (W2-hard-a) — e.g. 409 STORAGE_LOCATION_ZONE_IN_USE: moving a location that backs
      // live quotes to another zone. NEX-1168 — 400 STORAGE_LOCATION_COORDINATES_INVALID too.
      setFormError(knownApiErrorMessage(error) ?? 'No pudimos guardar el depósito. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove || removingRef.current) return;
    removingRef.current = true;
    setRemoving(true);
    setFormError(null);
    try {
      await onRemove();
    } catch (error) {
      setFormError(describeApiError(error));
    } finally {
      removingRef.current = false;
      setRemoving(false);
    }
  };

  const departmentOptions = departments.map((option) => ({ value: option.name, label: option.name }));
  const localitySelectOptions = localityOptions.map((option) => ({ value: option.name, label: option.name }));

  return (
    <FormPage
      title={isEditing ? 'Editar depósito' : 'Nuevo depósito'}
      onSubmit={handleSubmit}
      submitting={busy}
      notice={formError ? <Alert tone="critical">{formError}</Alert> : undefined}
      sections={[
        {
          id: 'ubicacion',
          title: 'Ubicación',
          children: (
            <>
              <FormField label="Departamento">
                {(field) => (
                  <Select
                    {...field}
                    options={departmentOptions}
                    value={department}
                    onChange={handleDepartmentChange}
                    placeholder="Elegí un departamento"
                  />
                )}
              </FormField>
              <FormField label="Localidad" disabled={!department}>
                {(field) => (
                  <Select
                    {...field}
                    options={localitySelectOptions}
                    value={locality}
                    onChange={setLocality}
                    placeholder={department ? 'Elegí una localidad' : 'Elegí un departamento primero'}
                  />
                )}
              </FormField>
              {addressSearch ? (
                <FormField
                  label="Dirección"
                  hint={
                    located
                      ? 'Dirección ubicada en el mapa: el transportista va a poder navegar hasta el depósito.'
                      : 'Elegí la dirección entre las sugerencias para que el transportista pueda navegar hasta el depósito. Si no aparece, escribila igual.'
                  }
                >
                  {(field) => (
                    <AddressAutocompleteField
                      search={addressSearch}
                      label="Dirección"
                      id={field.id}
                      aria-describedby={field['aria-describedby']}
                      disabled={field.disabled}
                      placeholder="Buscá la calle y el número"
                      value={address}
                      onType={typeAddress}
                      onResolve={(resolved) => {
                        setAddress(resolved.address);
                        setCoordinates({
                          latitude: resolved.latitude,
                          longitude: resolved.longitude,
                          placeId: resolved.placeId
                        });
                      }}
                    />
                  )}
                </FormField>
              ) : (
                <FormField label="Dirección">
                  {(field) => (
                    <Textarea
                      {...field}
                      value={address}
                      onChange={(event) => typeAddress(event.target.value)}
                      rows={2}
                      placeholder="Calle, número y referencias"
                    />
                  )}
                </FormField>
              )}
            </>
          )
        },
        {
          id: 'fotos',
          title: 'Fotos',
          description: `Opcional. Hasta ${MAX_PHOTOS} fotos en JPG, PNG o WEBP de hasta ${MAX_PHOTO_SIZE_BYTES / (1024 * 1024)} MB cada una.`,
          children: (
            <div className="flex flex-col gap-3">
              {photoError && <Alert tone="critical">{photoError}</Alert>}

              {photos.length > 0 && (
                <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
                  {photos.map((photo, index) => (
                    <li key={index}>
                      <FileThumb url={photo} label={`Foto ${index + 1} del depósito`} onRemove={() => removePhoto(index)} />
                    </li>
                  ))}
                </ul>
              )}

              {/* No file-picker button exists in the design system (spec D-4.3): a label around the
                  input is what opens the native picker. Styled as the outline button. The input is
                  `sr-only`, not `hidden`: still in the tab order (Space/Enter open the picker), with
                  the keyboard focus ring drawn on the label (`has-[:focus-visible]`, so a mouse click leaves
                  none) — the same input technique as DocumentUploadRow's. */}
              <label className="inline-flex h-11 w-fit cursor-pointer items-center rounded-pill border-[1.5px] border-primary px-5 text-ui-label text-primary hover:bg-primary-container has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary">
                Agregar fotos
                <input type="file" accept={ALLOWED_PHOTO_MIME_TYPES.join(',')} multiple onChange={handlePhotoFiles} className="sr-only" />
              </label>
            </div>
          )
        },
        // Storage responsive pass (2026-10-27, D-1): this used to sit in the sticky bar next to Cancelar and
        // Guardar; with three actions the bar took up to 40% of an iPhone SE screen. It lives at the end
        // of the form now, out of the bar.
        ...(isEditing && onRemove
          ? [
              {
                id: 'eliminar',
                title: 'Eliminar este depósito',
                children: (
                  // A text button in the critical tone. Not `Button tone="critical"`: a tone REPLACES the
                  // variant with a solid red fill, heavier than "Guardar depósito". Built from token
                  // classes, the way the pill toggles are (spec 2026-10-18 §2).
                  <div className="flex justify-start">
                    <button
                      type="button"
                      onClick={() => void handleRemove()}
                      disabled={busy}
                      aria-busy={removing || undefined}
                      className="-ml-5 inline-flex h-11 cursor-pointer items-center gap-2 rounded-pill px-5 text-ui-label text-tone-critical-fg outline-offset-2 hover:not-disabled:bg-tone-critical-bg focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:text-state-disabled-content"
                    >
                      <Trash size={16} />
                      Eliminar depósito
                    </button>
                  </div>
                )
              }
            ]
          : [])
      ]}
      actions={
        <StickyActionBar>
          <Button type="button" variant="text" size="xs" showArrow={false} onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" size="xs" showArrow={false} disabled={busy}>
            {submitting ? 'Guardando…' : 'Guardar depósito'}
          </Button>
        </StickyActionBar>
      }
    />
  );
}
